import {
    createEntityAdapter,
    createSlice,
    EntityState,
} from "@reduxjs/toolkit";
import { IComment } from "../../../api/comments";
import { RootState } from "../../../app/store";
import { addCommentThunk, fetchCommentsThunk } from "./commentsThunk";

export interface CommentsState {
  comments: EntityState<IComment, string>;
  isFetching: boolean;
  isAdding: boolean;
  fetchError: string | null;
  addError: string | null;
}

const commentsAdapter = createEntityAdapter<IComment, string>({
  selectId: (c) => c.comment_id,
});

const initialState: CommentsState = {
  comments: commentsAdapter.getInitialState({}),
  isFetching: false,
  isAdding: false,
  fetchError: null,
  addError: null,
};

const commentsSlice = createSlice({
  name: "comments",
  initialState,
  reducers: {
    clearCommentsState: (state) => {
      commentsAdapter.removeAll(state.comments);
      state.isFetching = false;
      state.isAdding = false;
      state.fetchError = null;
      state.addError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCommentsThunk.pending, (state) => {
        state.isFetching = true;
        state.fetchError = null;
        commentsAdapter.removeAll(state.comments);
      })
      .addCase(fetchCommentsThunk.fulfilled, (state, action) => {
        state.isFetching = false;
        commentsAdapter.setAll(state.comments, action.payload.comments || []);
      })
      .addCase(fetchCommentsThunk.rejected, (state, action) => {
        state.isFetching = false;
        state.fetchError =
          (action.payload as string) ||
          action.error.message ||
          "Failed to fetch comments";
      })
      .addCase(addCommentThunk.pending, (state) => {
        state.isAdding = true;
        state.addError = null;
      })
      .addCase(addCommentThunk.fulfilled, (state, action) => {
        state.isAdding = false;
        commentsAdapter.upsertOne(state.comments, action.payload);
      })
      .addCase(addCommentThunk.rejected, (state, action) => {
        state.isAdding = false;
        state.addError =
          (action.payload as string) ||
          action.error.message ||
          "Failed to add comment";
      });
  },
});

export const { clearCommentsState } = commentsSlice.actions;
export const commentsReducer = commentsSlice.reducer;

const commentsSelectors = commentsAdapter.getSelectors(
  (state: RootState) => state.commentsData.comments,
);

export const selectAllComments = commentsSelectors.selectAll;
export const selectCommentById = commentsSelectors.selectById;
export const selectCommentsTotal = commentsSelectors.selectTotal;

export const selectCommentsIsFetching = (state: RootState) =>
  state.commentsData.isFetching;
export const selectCommentsIsAdding = (state: RootState) =>
  state.commentsData.isAdding;
